import { createReducer, on } from "@ngrx/store";
import { BillingCycle, Buyer } from ".";
import { AdditionalItem, IEditionsState, ActionTrial, ActionCheckout } from "./model";
import { BillingCycle as EditionBillingCycle, MarketingSource } from "../../api/proxies";
import { assign } from "lodash";
import { AuthenticateResultModel } from "@api";
import { checkout, checkoutComplete, checkoutError, creditCardError, getEditions, getEditionsComplete, gotoOrderBump, loadAvailableAdditionalItems, loadAvailableAdditionalItemsComplete, loadAvailableAdditionalItemsError, loadAvailableAdditionalProducts, loadAvailableAdditionalProductsComplete, loadAvailableAdditionalProductsError, migrationNotSupported, patientNotAllowed, saveBasicData, saveBasicDataComplete, saveBasicDataError, selectEdition, setBuyer, setCreditCard, setMarketingSource } from "./actions";

export const SIMPLIFIED_FEATURE_KEY = "subscription-simplified";

export const initialSimplifiedState: IEditionsState = {
    popular: {
        monthly: "",
        annual: "",
    },
    editions: [],
    selectedEdition: undefined,
    selectedEditionHash: undefined,
    loading: false,
    creditCard: undefined,
    billingCycle: 2,
    totalChargedNow: 0,
    paymentMethod: 3,
    total: 0,
    buyer: {} as Buyer,
    step: 0,
    action: ActionTrial,
    trialDays: 0,
    availableAdditionalItems: [],
    additionalItems: [],
    additionalProducts: [],
    orderBump: undefined,
    authenticateResultModel: undefined,
    marketingSource: {} as MarketingSource,
};

export const getEditionsReducer = createReducer(
    initialSimplifiedState,
    on(getEditions, (state, { token }) => ({
        ...state,
        loading: true,
    })),
    on(getEditionsComplete, (state, { result }) => {
        return {
            ...state,
            ...result,
            loading: false,
        };
    }),
    on(loadAvailableAdditionalItems, (state) => ({
        ...state,
        loading: true,
    })),
    on(loadAvailableAdditionalItemsComplete, (state, { sections }) => {
        return {
            ...state,
            loading: false,
            availableAdditionalItems: sections,
        };
    }),
    on(loadAvailableAdditionalItemsError, (state, { error }) => ({
        ...state,
        loading: false,
    })),
    on(loadAvailableAdditionalProducts, (state) => ({
        ...state,
        loading: true,
    })),
    on(loadAvailableAdditionalProductsComplete, (state, { products }) => {
        return {
            ...state,
            loading: false,
            additionalProducts: products,
        };
    }),
    on(loadAvailableAdditionalProductsError, (state, { error }) => ({
        ...state,
        loading: false,
    })),
    on(selectEdition, (state, { edition, locked }) => {
        let billingCycle = state.billingCycle;
        if (edition.billingCycle) {
            billingCycle = edition.billingCycle === EditionBillingCycle._2 ? BillingCycle.Annual : BillingCycle.Monthly;
        }
        return {
            ...state,
            popular: {
                monthly: "",
                annual: "",
            },
            editions: [],
            selectedEdition: edition,
            billingCycle: billingCycle,
            loading: false,
            creditCard: null,
            total: 0,
            trialDays: edition.trialDays,
            paymentMethod: edition.paymentMethod,
            additionalItems: edition.additionalItems
                ? edition.additionalItems.map((item) => assign(new AdditionalItem(), item))
                : [],
            orderBump: edition.orderBump
                ? {
                    ...edition.orderBump,
                    items: edition.orderBump.items,
                    maxNumberOfInstallments: 6,
                }
                : null,
        };
    }),
    on(setMarketingSource, (state, { params }) => {
        return {
            ...state,
            marketingSource: MarketingSource.fromJS({
                source: params?.utm_source,
                medium: params?.utm_medium,
                campaign: params?.utm_campaign,
                content: params?.utm_content,
            }),
        };
    }),
    on(saveBasicData, (state) => ({
        ...state,
        loading: true,
    })),
    on(saveBasicDataComplete, (state, { email, name, phone, creatorProfile }) => ({
        ...state,
        buyer: {
            ...state.buyer,
            email,
            name,
            mobilePhone: phone,
            creatorProfile,
        },
        loading: false,
        //If is a patient, redirect to the professionals only disclaimer page
        step: creatorProfile === 10 ? 4 : 1,
    })),
    on(saveBasicDataError, (state, { error }) => ({
        ...state,
        loading: false,
        buyer: {} as Buyer,
    })),
    on(setCreditCard, (state, { creditCard }) => ({
        ...state,
        creditCard: creditCard,
    })),
    on(creditCardError, (state, { error }) => ({
        ...state,
        error: error,
    })),
    on(selectEdition, (state) => {
        if (state.selectedEdition) {
            let total: number =
                state.billingCycle === 1
                    ? state.selectedEdition.price.monthly
                    : state.selectedEdition.price.annual.once;
            if (state.additionalItems && state.additionalItems.length > 0) {
                for (let item of state.additionalItems) {
                    if (item.quantity > 0) {
                        total += item.subtotal * (state.billingCycle === 1 ? 1 : 12);
                    }
                }
            }

            return {
                ...state,
                loading: false,
                total,
                totalChargedNow: state.action !== ActionTrial || state.selectedEdition.trialDays === 0 ? total : 0,
            };
        }
        return { ...state };
    }),
    on(checkout, (state) => ({
        ...state,
        loading: true,
    })),
    on(checkoutComplete, (state, { result }) => {
        return {
            ...state,
            authenticateResultModel: AuthenticateResultModel.fromJS(result.authenticateResultModel),
            loading: false,
        };
    }),
    on(gotoOrderBump, (state, { }) => {
        return {
            ...state,
            loading: false,
            step: 2,
        };
    }),
    on(checkoutError, (state) => ({
        ...state,
        loading: false,
    })),
    on(setBuyer, (state, { buyer }) => ({
        ...state,
        buyer: buyer,
        step: 1,
        action: ActionCheckout,
        loading: false,
        termsOfServiceAccepted: true,
    })),
    on(migrationNotSupported, (state) => ({
        ...state,
        step: 3,
        loading: false,
    })),
    on(patientNotAllowed, (state) => ({
        ...state,
        step: 4,
        loading: false,
    }))
);
